import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Chip,
  Divider,
  IconButton,
  CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import { useDocument, useDeleteDocument } from '../../../hooks/useDocs';
import { DOCUMENT_STATUS_COLORS } from '../constants';
import { APP_PAGES } from '../../../pages';
import { toast } from '../../../Utils/toast';
import ConfirmationDialog from '../../../Components/ConfirmationDialog';

const Detail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { document, isLoading, error } = useDocument(id || null); // Auto-fetches when id changes
  const { deleteDoc, isDeleting } = useDeleteDocument();
  const [isDownloading, setIsDownloading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const handleDownload = async () => {
    if (!document || !id) {
      toast.error('Document information not available');
      return;
    }
    
    // Use route param id as fallback if document.id is undefined
    const documentId = document.id || id;
    
    if (!documentId) {
      console.error('Document ID is missing:', { document, id });
      toast.error('Document ID is missing');
      return;
    }
    
    setIsDownloading(true);
    try {
      const { downloadDocument } = await import('../../../Utils/docsApi');
      const blob = await downloadDocument(documentId);
      
      // Validate blob
      if (!blob || blob.size === 0) {
        throw new Error('Received empty file');
      }
      
      const url = window.URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = url;
      a.download = document.displayName || document.name;
      window.document.body.appendChild(a);
      a.click();
      
      // Cleanup
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        window.document.body.removeChild(a);
      }, 100);
      
      toast.success('Download started');
    } catch (error: any) {
      console.error('Download error:', error);
      const errorMessage = error?.message || error?.response?.data?.message || 'Failed to download document';
      toast.error(errorMessage);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!document || !id) return;
    setDeleteDialogOpen(false);
    // Use route param id as fallback if document.id is undefined
    const documentId = document.id || id;
    try {
      await deleteDoc(documentId);
      toast.success('Document deleted successfully');
      // Navigate back to previous page, or library if no history
      navigate(-1);
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ready':
        return DOCUMENT_STATUS_COLORS.ready;
      case 'processing':
        return DOCUMENT_STATUS_COLORS.processing;
      case 'failed':
        return DOCUMENT_STATUS_COLORS.failed;
      default:
        return '#666666';
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !document) {
    return (
      <Box sx={{ flexGrow: 1, bgcolor: '#F8F8F8', minHeight: '100vh', pb: 4 }}>
        <Container maxWidth="md" sx={{ pt: 4 }}>
          <Paper sx={{ p: 3 }}>
            <Typography color="error">{error || 'Document not found'}</Typography>
            <Button sx={{ mt: 2 }} onClick={() => navigate(-1)}>
              Back
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, bgcolor: '#F8F8F8', minHeight: '100vh', pb: 4 }}>
      <Container maxWidth="md" sx={{ pt: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ mb: 2 }}
        >
          Back
        </Button>

        <Paper sx={{ p: 4 }}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
            <Box>
              <Typography variant="h1" sx={{ mb: 1 }}>
                {document.displayName || document.name}
              </Typography>
              <Chip
                label={document.status}
                sx={{
                  bgcolor: getStatusColor(document.status),
                  color: '#fff',
                  textTransform: 'capitalize',
                }}
              />
            </Box>
            <Box display="flex" gap={1}>
              <IconButton
                onClick={handleDownload}
                disabled={isDownloading}
                aria-label="Download document"
                data-testid={`docs-document-${document.id}-download-button`}
              >
                {isDownloading ? <CircularProgress size={24} /> : <DownloadIcon />}
              </IconButton>
              <IconButton
                onClick={handleDeleteClick}
                disabled={isDeleting}
                aria-label="Delete document"
                data-testid={`docs-document-${document.id}-delete-button`}
                color="error"
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box display="flex" flexDirection="column" gap={2}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Name
              </Typography>
              <Typography variant="body1">{document.name}</Typography>
            </Box>

            {document.displayName && (
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Display Name
                </Typography>
                <Typography variant="body1">{document.displayName}</Typography>
              </Box>
            )}

            <Box>
              <Typography variant="body2" color="text.secondary">
                Source
              </Typography>
              <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>
                {document.source}
              </Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">
                Created
              </Typography>
              <Typography variant="body1">
                {new Date(document.createdAt).toLocaleString()}
              </Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">
                Updated
              </Typography>
              <Typography variant="body1">
                {new Date(document.updatedAt).toLocaleString()}
              </Typography>
            </Box>

            {document.metadata && Object.keys(document.metadata).length > 0 && (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Metadata
                </Typography>
                <Paper sx={{ p: 2, bgcolor: '#F8F8F8' }}>
                  <pre style={{ margin: 0, fontSize: '0.875rem' }}>
                    {JSON.stringify(document.metadata, null, 2)}
                  </pre>
                </Paper>
              </Box>
            )}
          </Box>
        </Paper>
      </Container>

      <ConfirmationDialog
        open={deleteDialogOpen}
        title="Delete Document"
        message={`Are you sure you want to delete "${document.displayName || document.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteDialogOpen(false)}
        confirmColor="error"
        isLoading={isDeleting}
      />
    </Box>
  );
};

export default Detail;
